import { createGuard } from 'src_common/common/createGuard';
import * as t from 'io-ts';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import config from 'src/config';
import { PropsInnerType } from './CombinationBetNew';
import { FreeBetType } from './BonusEngineFreeBets/FreeBetSelector';

const PropsInIO = t.interface({
    assignedToMe: t.unknown,
    bet: t.unknown,
    combinations: t.unknown,
    currency: t.unknown,
    focusedBet: t.unknown,
    legs: t.unknown,
    onBlur: t.unknown,
    onChange: t.unknown,
    onFocus: t.unknown,
    onRemove: t.unknown,
});

type PropsInType = t.TypeOf<typeof PropsInIO>;
const isPropsInIO = createGuard(PropsInIO);

const CombinationsLegsIO = t.interface({
    selection: t.interface({
        id: t.number,
    }),
    market: t.interface({
        id: t.number,
    }),
    event: t.interface({
        id: t.number,
    }),
    type: t.string,
    priceType: t.string,
    price: t.union([
        t.null,
        t.undefined,
        t.interface({
            f: t.string,
            d: t.number,
        }),
    ]),
});

const CombinationsIO = t.interface({
    delay: t.number,
    legs: t.array(CombinationsLegsIO),
    potentialReturns: t.union([t.null, t.number]),
    price: t.union([
        t.null,
        t.undefined,
        t.interface({
            f: t.string,
            d: t.number,
        }),
    ]),
    ewOffered: t.boolean,
    name: t.string,
    visibleForCustomer: t.boolean,
    maxStake: t.number,
    potentialReturnsEw: t.union([t.null, t.number]),
    numLines: t.number,
    type: t.string,
    visibleForBackend: t.boolean,
    freeBets: t.union([t.undefined, t.null, t.array(t.string)]),
});

type CombinationsType = t.TypeOf<typeof CombinationsIO>;
const isCombinationsIO = createGuard(t.array(CombinationsIO));

const BetTypeIO = t.interface({
    priceType: t.union([t.record(t.string, t.string), t.undefined, t.null]),
    legs: t.array(t.string),
    price: t.union([
        t.null,
        t.undefined,
        t.string,
        t.interface({
            f: t.string,
            d: t.number,
        }),
    ]),
    ewOffered: t.union([t.boolean, t.undefined, t.null]),
    name: t.union([t.string, t.undefined, t.null]),
    eachWay: t.boolean,
    uid: t.string,
    stakePerLine: t.union([t.number, t.string]),
    numLines: t.union([t.number, t.undefined, t.null]),
    type: t.string,
    isReferred: t.union([t.boolean, t.undefined, t.null]),
    errors: t.union([t.array(t.unknown), t.undefined, t.null]),
    potentialReturnsAt: t.union([t.number, t.undefined, t.null]),
    potentialReturns: t.union([t.number, t.undefined, t.null]),
    maxStake: t.union([t.number, t.undefined, t.null]),
    freeBets: t.union([t.undefined, t.null, t.array(t.string)]),
});

type BetTypeType = t.TypeOf<typeof BetTypeIO>;
const isBetTypeIO = createGuard(BetTypeIO);
const isFocusedBetIO = createGuard(t.union([BetTypeIO, t.null]));

const CurrencyTypeIO = t.interface({
    readOnly: t.boolean,
    hidden: t.boolean,
    value: t.string,
});

type CurrencyTypeType = t.TypeOf<typeof CurrencyTypeIO>;
const isCurrencyTypeIO = createGuard(CurrencyTypeIO);

const LegReferenceIO = t.interface({
    geniusMarketId: t.string,
    geniusFixtureId: t.string,
});

const LegTypeIO = t.interface({
    selectionId: t.union([t.number, t.undefined]),
    marketId: t.union([t.number, t.undefined]),
    eventId: t.union([t.number, t.undefined]),
    price: t.union([t.string, t.undefined, t.null]),
    isMarketSp: t.union([t.boolean, t.undefined, t.null]),
    priceType: t.union([t.string, t.undefined, t.null]),
    eachWay: t.union([t.boolean, t.undefined, t.null]),
    uid: t.string,
    type: t.union([t.string, t.undefined]),
    name: t.union([t.string, t.undefined, t.null]),
    legReference: t.union([LegReferenceIO, t.undefined, t.null]),
    provider: t.union([t.string, t.undefined, t.null]),
    internalEventId: t.union([t.number, t.undefined]),
});

type LegTypeType = t.TypeOf<typeof LegTypeIO>;

const LegsTypeIO = t.record(
    t.string,
    t.interface({
        selectionId: t.union([t.number, t.undefined]),
        marketId: t.union([t.number, t.undefined]),
        eventId: t.union([t.number, t.undefined]),
        price: t.union([t.string, t.undefined, t.null]),
        isMarketSp: t.union([t.boolean, t.undefined, t.null]),
        priceType: t.union([t.string, t.undefined, t.null]),
        eachWay: t.union([t.boolean, t.undefined, t.null]),
        uid: t.string,
        type: t.union([t.string, t.undefined]),
        name: t.union([t.string, t.undefined, t.null]),
        legReference: t.union([LegReferenceIO, t.undefined, t.null]),
        provider: t.union([t.string, t.undefined, t.null]),
        internalEventId: t.union([t.number, t.undefined]),
    })
);

type LegsTypeType = t.TypeOf<typeof LegsTypeIO>;
const isLegsTypeIO = createGuard(LegsTypeIO);

export class CombinationBetState {
    @observable public stakePerLine: string;

    public constructor(private readonly propsIn: PropsInnerType) {
        //@ts-expect-error
        this.stakePerLine = propsIn.bet.stakePerLine.toString() ?? 0;
        makeAutoObservable(this);
    }

    public handleChangeStake = (value: string): void => {
        const regex = /[.,]/;
        const isDecimalCheck = regex.exec(value) !== null;
        if (isDecimalCheck) {
            const splitValue = value.split(/[.,]/);

            if (splitValue.length === 2) {
                const secondSplitValue = splitValue[1] ?? null;
                if (secondSplitValue !== null && secondSplitValue.length <= 2) {
                    this.setStakePerLine(value);
                }
            }
        } else {
            this.setStakePerLine(value);
        }
    };

    @action public setStakePerLine = (stakePerLine: string): void => {
        this.stakePerLine = stakePerLine;
    };

    @action public onChangeStakePerLine = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.handleChangeStake(e.target.value);
    };

    @computed public get props(): PropsInType | null {
        if (isPropsInIO(this.propsIn)) {
            return this.propsIn;
        }
        console.log('Error - isPropsInIO', this.propsIn);
        return null;
    }

    @computed public get combinations(): Array<CombinationsType> {
        if (this.props === null) {
            return [];
        }
        if (isCombinationsIO(this.props.combinations)) {
            return this.props.combinations;
        }
        console.log('Error - isCombinationsIO', this.props.combinations);
        return [];
    }

    @computed public get bet(): BetTypeType | null {
        if (this.props === null) {
            return null;
        }
        if (isBetTypeIO(this.props.bet)) {
            return this.props.bet;
        }
        console.log('Error - isBetTypeIO', this.props.bet);
        return null;
    }

    @computed public get focusedBet(): BetTypeType | null {
        if (this.props === null) {
            return null;
        }
        if (isFocusedBetIO(this.props.focusedBet)) {
            return this.props.focusedBet;
        }
        console.log('Error - isFocusedBetIO', this.props.focusedBet);
        return null;
    }

    @computed public get currency(): CurrencyTypeType | null {
        if (this.props === null) {
            return null;
        }
        if (isCurrencyTypeIO(this.props.currency)) {
            return this.props.currency;
        }
        console.log('Error - isCurrencyTypeIO', this.props.currency);
        return null;
    }

    @computed public get legs(): LegsTypeType {
        if (this.props === null) {
            return {};
        }

        if (isLegsTypeIO(this.props.legs)) {
            return this.props.legs;
        } else {
            console.log('Error - isLegsTypeIO', this.props.legs);
        }
        return {};
    }

    @computed public get isFocused(): boolean {
        if (this.focusedBet !== null && this.bet !== null) {
            return this.focusedBet.uid === this.bet.uid;
        }
        return false;
    }

    @computed public get isReferred(): boolean {
        if (this.bet !== null) {
            return this.bet.isReferred === true;
        }
        return false;
    }

    @computed public get hasSP(): boolean {
        if (this.bet !== null) {
            return (
                this.bet.legs.map((leg) => this.legs[leg]).filter((leg) => leg !== undefined && leg.priceType === 'sp')
                    .length > 0
            );
        }
        return false;
    }

    @computed public get currencySymbol(): string {
        if (this.currency !== null) {
            if (
                this.currency.value === 'GBP' ||
                this.currency.value === 'EUR' ||
                this.currency.value === 'XTS' ||
                this.currency.value === 'CAD' ||
                this.currency.value === 'NZD'
            ) {
                return config.symbols[this.currency.value];
            }
            return this.currency.value;
        }

        return 'n/a';
    }

    @computed public get combinationFound(): CombinationsType | null {
        return this.combinations.find((c) => this.bet !== null && c.type === this.bet.type) ?? null;
    }

    @computed public get possibleReturnsForReferral(): number | 'n/a' {
        return this.hasSP
            ? 'n/a'
            : this.combinationFound === null
              ? 'n/a'
              : (this.combinationFound.potentialReturns ?? 'n/a');
    }

    @computed public get betStake(): number {
        if (this.bet !== null) {
            return typeof this.bet.stakePerLine === 'string'
                ? parseFloat(this.bet.stakePerLine)
                : this.bet.stakePerLine;
        }
        return 0;
    }

    @computed public get stake(): number {
        const stake = parseFloat(this.stakePerLine);

        if (isNaN(stake)) {
            return this.betStake;
        }

        return stake;
    }

    @computed public get possibleReturnsEstimated(): boolean {
        return !this.hasSP && this.bet !== null && this.bet.potentialReturnsAt !== Math.round(this.stake * 100);
    }

    @computed public get price(): string {
        if (this.bet !== null && this.bet.price !== null && this.bet.price !== undefined) {
            return typeof this.bet.price === 'string' ? this.bet.price : this.bet.price.f;
        }
        return 'n/a';
    }

    @computed public get possibleReturnsDefault(): number | 'n/a' {
        const price = this.price.split('/');
        const numeratorRaw = price[0] ?? null;
        const denominatorRaw = price[1] ?? null;

        const numerator = numeratorRaw === null ? 0 : parseInt(numeratorRaw, 10);
        const denominator = denominatorRaw === null ? 1 : parseInt(denominatorRaw, 10);

        if (this.bet !== null) {
            return Math.round(Math.round(100 * this.betStake) * (numerator + denominator)) / denominator;
        }

        return 'n/a';
    }

    @computed public get possibleReturns(): number | 'n/a' {
        if (this.isReferred) {
            return this.possibleReturnsForReferral;
        }

        return this.hasSP
            ? 'n/a'
            : this.possibleReturnsEstimated
              ? this.possibleReturnsDefault
              : this.bet === null
                ? 'n/a'
                : (this.bet.potentialReturns ?? 'n/a');
    }

    @action public cleanStake = (): void => {
        this.stakePerLine = this.stakePerLine == '0' ? '' : this.stakePerLine;
    };

    @action public saveStake = (): void => {
        if (this.stake === this.betStake) {
            this.handleChangeStake(this.betStake.toString());
        } else {
            this.propsIn.onChange({
                stakePerLine: this.stake.toFixed(2),
            });
        }
    };

    @action public remove = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        e.preventDefault();

        this.propsIn.onBlur();
        this.propsIn.onRemove();
    };

    @action public handleBlur = (): void => {
        this.propsIn.onBlur();
    };

    @action public handleFocus = (): void => {
        this.propsIn.onFocus();
    };

    @action public onChangeEachWay = (): void => {
        if (this.bet !== null) {
            this.propsIn.onChange({
                eachWay: !this.bet.eachWay,
            });
        }
    };

    @computed public get assignedToMe(): boolean | null | undefined {
        return this.propsIn.assignedToMe;
    }

    @computed public get legsForCombination(): Array<LegTypeType> {
        const legsForCombination: Array<LegTypeType> = [];

        if (this.bet === null) {
            return [];
        }

        const legs = Object.values(this.legs);

        for (const leg of legs) {
            if (this.bet.legs.includes(leg.uid)) {
                legsForCombination.push(leg);
            }
        }

        return legsForCombination;
    }

    @computed public get errorBlackBox(): string | undefined {
        const betErrors = this.bet?.errors ?? [];
        if (betErrors.length > 0) {
            console.log('Errors: ', betErrors);
            return 'Error found';
        }

        return undefined;

        // const errors = mapErrors(betErrors);
        // return listMappedErrors(errors, {
        //     'SelectedBet:not-found': (details: unknown) => {
        //         if (details.field === 'eachWay') {
        //             return 'This selection doesn\'t allow each way terms.';
        //         }

        //         return getErrorMessage(details);
        //     }
        // }).map((error) => error.constructor === String ? error : error.props.children).join('\n');
    }

    @computed public get betBonusEngineFreeBets(): Array<FreeBetType> {
        if (this.bet === null) {
            return [];
        }

        return this.propsIn.bonusEngineFreeBets.filter((entry) => this.bet !== null && entry.type === this.bet.type);
    }
}
